import httpService from '@/core/http/http.service';
import utilities from '@/shared/utilities.service';
import siteContextStore from '@/shared/content/site-context.store';

const getCurrentLanguage = () => siteContextStore.settings.language.toLowerCase();

export class TempBasketApiClient {
    private readonly baseUrl: string = '';

    get(): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default?language=${getCurrentLanguage()}`;
        return httpService.get<TempBasketViewModel>(apiUrl).then(utilities.unwrapModel);
    }

    add(request: TempAddToBasketRequest): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request).then(utilities.unwrapModel);
    }

    replaceItem(request: TempReplaceItemRequestItemRequest): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/replace?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request).then(utilities.unwrapModel);
    }

    addPickAndPack(request: TempAddToBasketRequest): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/pickandpack?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request).then(utilities.unwrapModel);
    }

    addClickAndCollect(request: TempAddClickAndCollectToBasketRequestItem): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/clickandcollect?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request).then(utilities.unwrapModel);
    }

    addInstant(request: TempAddInstantToBasketRequestItem): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/instant?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request).then(utilities.unwrapModel);
    }

    addMultiple(request: TempAddMultipleToBasketRequest): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/multiple?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request).then(utilities.unwrapModel);
    }

    addOffer(quotationId: string): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/quotation/${quotationId}?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl).then(utilities.unwrapModel);
    }

    addOfferItem(quotationId: string, lineItemId: string): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/quotation/${quotationId}/${lineItemId}?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl).then(utilities.unwrapModel);
    }

    addOutletItem(request: TempOutletItemRequestItem, messageId?: string): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/outlet?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request, messageId).then(utilities.unwrapModel);
    }

    outletItemInStock(request: TempOutletItemRequestItem): Promise<boolean> {
        const apiUrl = `${this.baseUrl}/api/basket/default/outletinstock?language=${getCurrentLanguage()}`;
        return httpService.post<TempOutletStockStatusResponseModel>(apiUrl, request).then(utilities.unwrapModel);
    }

    addB2b(request: TempAddToBasketRequest, id: string, messageId?: string): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/${id}?language=${getCurrentLanguage()}`;
        return httpService.put<TempBasketViewModel>(apiUrl, request, messageId).then(utilities.unwrapModel);
    }

    removeItem(request: TempRequestItem, id: string, messageId?: string): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/${id}?language=${getCurrentLanguage()}`;
        return httpService.delete<TempBasketViewModel>(apiUrl, request, messageId).then(utilities.unwrapModel);
    }

    updateB2bExpress(request: TempUpdateB2bExpressRequest, id: string, messageId?: string): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/expressDelivery/${id}?language=${getCurrentLanguage()}`;
        return httpService.put<TempBasketViewModel>(apiUrl, request, messageId).then(utilities.unwrapModel);
    }

    addB2bExpress(request: TempAddToBasketRequest, messageId?: string): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/add-partner-express?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request, messageId).then(utilities.unwrapModel);
    }

    addOrUpdateModuleConfiguration(moduleConfigurationId: string, request: TmpAddOrUpdateModuleConfigurationRequest, messageId?: string): Promise<TempBasketViewModel> {
        const apiUrl = `${this.baseUrl}/api/basket/default/module-configuration/${moduleConfigurationId}?language=${getCurrentLanguage()}`;
        return httpService.post<TempBasketViewModel>(apiUrl, request, messageId).then(utilities.unwrapModel);
    }
}

export const TempBasketApiClientService = new TempBasketApiClient();

export type TempBasketViewModel = any;

type TempUpdateB2bExpressRequest = {
    expressDelivery: boolean;
}

export type TempAddInstantToBasketRequestItem = {
    sku: string;
};

export type TempRequestItem = {};

export type TempAddToBasketRequestItem = {
    sku: string;
    quantity: number;
    delta?: boolean;
};

export type TempAddClickAndCollectToBasketRequestItem = {
    sku: string;
    quantity: number;
    storeId: string;
};

export type TempReplaceItemRequestItem = {
    sourceId: string;
    sourceLineItemId: string;
    targetId: string;
};

export type TempAddToBasketRequest = TempAddToBasketRequestItem;
export type TempAddMultipleToBasketRequest = TempAddToBasketRequestItem[];
export type TempReplaceItemRequestItemRequest = {
    replacePairs: TempReplaceItemRequestItem[];
};

export type TempOutletItemRequestItem = {
    recId: number;
    quantity: number;
};

type TempOutletStockStatusResponseModel = {
    messages: string[];
    model: boolean;
}

export type TmpAddOrUpdateModuleConfigurationRequest = {
    configuration: TmpModuleConfiguration;
    modules: TmpModuleConfigurationItem[];
}

export type TmpModuleConfiguration = {
    name: string;
    depth: number;
    height: number;
    width: number;
    image: string;
}

export type TmpModuleConfigurationItem = {
    sku: string;
    quantity: number;
}
